import { DocsLayout } from '../components/DocsLayout';

export function BrowserTiersPage() {
  return (
    <DocsLayout
      title="Browser tiers"
      updated="May 7, 2026"
      summary="Rynora drives real browsers three different ways. The default works for most teams; the alternatives matter when you outgrow it."
    >
      <h2>Why three tiers?</h2>
      <p>
        Test automation is two problems wedged together: <em>driving</em> a browser and <em>watching</em> it while it
        runs. Most platforms force one trade-off; Rynora lets you pick. The active tier is set per-environment from the
        admin console under Runner → Browser tier.
      </p>

      <h2>The lineup</h2>
      <table>
        <thead>
          <tr>
            <th>Tier</th>
            <th>How it drives</th>
            <th>How you watch</th>
            <th>Best for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>CDP Screencast</strong> (default)
            </td>
            <td>Chrome DevTools Protocol → Playwright</td>
            <td>Streams JPEG frames over WebSocket</td>
            <td>Speed and the lowest infra footprint. Fits the free Oracle VM.</td>
          </tr>
          <tr>
            <td>
              <strong>Docker noVNC</strong>
            </td>
            <td>Headless Chrome inside a Docker container</td>
            <td>Full VNC desktop, viewable in the browser</td>
            <td>Pixel-perfect reviews, manual takeover, multi-monitor flows.</td>
          </tr>
          <tr>
            <td>
              <strong>Steel WebRTC</strong>
            </td>
            <td>Steel.dev managed browser via WebRTC</td>
            <td>Sub-100ms peer-to-peer video</td>
            <td>Low-latency interactive sessions when the team's distributed.</td>
          </tr>
        </tbody>
      </table>

      <h2>Picking a tier</h2>
      <h3>Start with CDP Screencast</h3>
      <p>
        It's the cheapest, fastest, and what every account starts on. CDP can drive any Chromium-based browser (Chrome,
        Edge, Brave) and Playwright handles Firefox + WebKit natively. Frame streaming is good enough for review and
        good enough to debug.
      </p>

      <h3>Move to Docker noVNC if…</h3>
      <ul>
        <li>You need to manually take over a stuck flow.</li>
        <li>You want pixel-accurate visual diffing.</li>
        <li>You're testing flows that span more than one window.</li>
      </ul>

      <h3>Move to Steel WebRTC if…</h3>
      <ul>
        <li>Your testers are spread across continents and screencast latency is hurting them.</li>
        <li>You want to record a session and replay it interactively.</li>
        <li>You're already using Steel for crawling / data extraction.</li>
      </ul>

      <h2>Switching tiers</h2>
      <p>
        Open <code>admin.rynora.app</code> → <strong>Settings</strong> → <strong>Runner</strong> →{' '}
        <strong>Browser tier</strong>. Switching is instant for new runs; in-flight runs finish on whatever tier they
        started.
      </p>

      <h2>Caveats</h2>
      <ul>
        <li>
          Docker noVNC needs ~1GB more RAM per concurrent session — bump <code>RYNORA_MAX_CONCURRENT_SESSIONS</code>{' '}
          down if you stay on the free VM.
        </li>
        <li>Steel WebRTC needs a Steel API key (free tier available) and outbound TURN traffic.</li>
        <li>
          CDP Screencast frames are JPEG, which means no transparent overlays. The other two tiers handle this fine.
        </li>
      </ul>
    </DocsLayout>
  );
}
